// src/pages/feedback/FeedbackListPage.tsx
//
// 피드백 게시판 목록.
//
// 필터 상태의 정본은 **URL 쿼리스트링**이다(useSearchParams). state 를 따로
// 두고 양방향 동기화하면 두 소스가 어긋나는 순간이 반드시 생기므로, URL 을
// 읽어 params 를 파생시키고 변경은 URL 에만 쓴다. 덕분에
//  · "이 필터 링크 봐 주세요" 가 그대로 동작하고
//  · 상세로 들어갔다 뒤로가기 하면 필터가 살아 있으며
//  · 기본값은 URL 에 남지 않아 주소가 깨끗하다.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Pagination,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import ConsoleHeader, {
  ConsoleHeaderMeta,
  LedgerFrame,
} from "../../components/common/ConsoleHeader.tsx";
import { designTokens } from "../../theme.ts";
import { useFeedbackApi } from "../../hooks/useFeedbackApi.ts";
import {
  DEFAULT_FEEDBACK_PARAMS,
  FeedbackListParams,
  FeedbackPostDto,
  FeedbackSort,
  FeedbackStatus,
  FeedbackType,
} from "../../contracts/feedback/types.ts";
import {
  STATUS_ORDER,
  TYPE_ORDER,
  typeLabel,
} from "./components/feedbackVisuals.ts";
import FeedbackSearchBar from "./components/FeedbackSearchBar.tsx";
import FeedbackRow from "./components/FeedbackRow.tsx";

const T = designTokens;

const C = {
  surface: "#FFFFFF",
  border: "#E4E7EA",
  ink: "#171A1E",
  sub: "#6B7178",
  accent: T.cargo,
  neutral: "#F2F4F5",
};

/** 총괄 확정: 페이지 크기 20 고정. */
const PAGE_SIZE = 20;

const SORTS: FeedbackSort[] = [
  "created_at_desc",
  "created_at_asc",
  "updated_at_desc",
  "updated_at_asc",
];

/** URL → params. 알 수 없는 값은 조용히 기본값으로 떨어뜨린다(서버와 같은 관용). */
function parseParams(sp: URLSearchParams): FeedbackListParams {
  const rawCategory = sp.get("category") ?? "";
  const rawStatus = sp.get("status") ?? "";
  const rawSort = sp.get("sort") ?? "";
  const rawPage = Number(sp.get("page"));

  return {
    q: sp.get("q") ?? "",
    category: (TYPE_ORDER as string[]).includes(rawCategory)
      ? (rawCategory as FeedbackType)
      : "",
    status: (STATUS_ORDER as string[]).includes(rawStatus)
      ? (rawStatus as FeedbackStatus)
      : "",
    sort: (SORTS as string[]).includes(rawSort)
      ? (rawSort as FeedbackSort)
      : DEFAULT_FEEDBACK_PARAMS.sort,
    page: Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 0,
    size: PAGE_SIZE,
  };
}

/** params → URL. 기본값은 파라미터를 남기지 않는다. */
function serializeParams(p: FeedbackListParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (p.q) sp.set("q", p.q);
  if (p.category) sp.set("category", p.category);
  if (p.status) sp.set("status", p.status);
  if (p.sort && p.sort !== DEFAULT_FEEDBACK_PARAMS.sort) sp.set("sort", p.sort);
  if (p.page) sp.set("page", String(p.page));
  return sp;
}

export default function FeedbackListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const inDashboard = location.pathname.startsWith("/dashboard");
  const basePath = inDashboard ? "/dashboard/feedback" : "/feedback";

  const { listPosts } = useFeedbackApi();

  const searchKey = searchParams.toString();
  const params = useMemo(
    () => parseParams(searchParams),
    // searchParams 는 location.search 기준으로 메모되어 있다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchKey]
  );

  const [items, setItems] = useState<FeedbackPostDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  // 조회 실패를 빈 상태와 구분해서 들고 있는다. 실패 시에도 items 는 비므로
  // 이 플래그가 없으면 네트워크·서버 오류가 "검색 결과 없음" 카드로 위장되고,
  // 스낵바는 4초 뒤 사라져 사용자는 필터만 계속 바꿔 보게 된다.
  const [listError, setListError] = useState<string | null>(null);
  const [snack, setSnack] = useState<
    { msg: string; severity: "error" | "success" } | null
  >(null);

  // 디바운스된 검색과 탭 전환이 겹치면 오래된 응답이 최신 응답을 덮는
  // 경합이 실제로 발생한다. 요청 시퀀스를 두고 최신 응답만 반영한다.
  const reqIdRef = useRef(0);

  const setParams = useCallback(
    (next: FeedbackListParams) => {
      setSearchParams(serializeParams(next), { replace: true });
    },
    [setSearchParams]
  );

  const fetchList = useCallback(
    async (p: FeedbackListParams) => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      setListError(null);
      try {
        const res = await listPosts(p);
        if (myReq !== reqIdRef.current) return; // 뒤늦은 응답 폐기
        setItems(res.items ?? []);
        setTotal(res.total ?? 0);
      } catch (e: any) {
        if (myReq !== reqIdRef.current) return;
        setItems([]);
        setTotal(0);
        const msg = e?.message ?? "목록을 불러오지 못했습니다.";
        setListError(msg);
        setSnack({ msg, severity: "error" });
      } finally {
        if (myReq === reqIdRef.current) {
          setLoading(false);
          setLoaded(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    fetchList(params);
  }, [params, fetchList]);

  // 마지막 페이지의 마지막 글이 지워지면 현재 page 가 범위를 벗어난다.
  // 응답이 온 뒤 마지막 유효 페이지로 되돌린다.
  useEffect(() => {
    const page = params.page ?? 0;
    if (loading || total === 0 || page === 0) return;
    if (page * PAGE_SIZE < total) return;
    setParams({ ...params, page: Math.max(0, Math.ceil(total / PAGE_SIZE) - 1) });
  }, [loading, total, params, setParams]);

  // 삭제 후 목록으로 돌아올 때 넘겨받은 플래시 메시지. 한 번 읽고 지운다.
  const flash = (location.state as { flash?: string } | null)?.flash;
  useEffect(() => {
    if (!flash) return;
    setSnack({ msg: flash, severity: "success" });
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash]);

  const hasFilter = Boolean(params.q || params.category || params.status);
  const isEmpty = loaded && !loading && !listError && items.length === 0;
  const pageCount = Math.ceil(total / PAGE_SIZE);

  return (
    <Box
      sx={{
        fontFamily: T.fonts.sans,
        p: inDashboard ? 0 : { xs: 2, md: 3 },
      }}
    >
      <ConsoleHeader
        code="MARS · FEEDBACK · INDEX"
        title="피드백 게시판"
        meta={<ConsoleHeaderMeta recordCountLabel="게시글" count={total} />}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate(`${basePath}/create`)}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            글쓰기
          </Button>
        }
      />

      {/* 유형 탭 — 탭별 건수 배지는 없다(서버 facet count 미제공).
          TODO(feedback): 유형 탭 건수 배지 — 서버 facet count API 필요 */}
      <Box sx={{ borderBottom: `1px solid ${C.border}`, mb: 2.5 }}>
        <Tabs
          value={params.category ?? ""}
          onChange={(_e, v) => setParams({ ...params, category: v, page: 0 })}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="유형 필터"
        >
          <Tab value="" label="전체" />
          {TYPE_ORDER.map((t) => (
            <Tab key={t} value={t} label={typeLabel(t)} />
          ))}
        </Tabs>
      </Box>

      <FeedbackSearchBar value={params} onChange={setParams} />

      <Box
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 13,
          color: C.sub,
          fontVariantNumeric: "tabular-nums",
          mb: 1.5,
        }}
      >
        전체 {total.toLocaleString("ko-KR")}건 중{" "}
        <Box component="span" sx={{ color: C.ink, fontWeight: 600 }}>
          {items.length.toLocaleString("ko-KR")}건
        </Box>{" "}
        표시
      </Box>

      {/* 첫 로드 */}
      {loading && !loaded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 10,
          }}
        >
          <CircularProgress size={26} sx={{ color: C.accent }} />
          <Box sx={{ fontFamily: T.fonts.sans, fontSize: 13, color: C.sub }}>
            피드백을 불러오는 중입니다…
          </Box>
        </Box>
      )}

      {/* 조회 실패 — 빈 상태와 구분해서 원인과 재시도를 화면에 남긴다.
          FeedbackDetailPage 의 에러 처리와 같은 패턴이다. */}
      {listError && !loading && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchList(params)}>
              다시 시도
            </Button>
          }
        >
          {listError}
        </Alert>
      )}

      {/* 빈 상태 — "아직 없음"과 "조건에 안 맞음"을 반드시 구분한다 */}
      {isEmpty && (
        <EmptyState
          filtered={hasFilter}
          onCreate={() => navigate(`${basePath}/create`)}
          onReset={() => setParams({ ...DEFAULT_FEEDBACK_PARAMS })}
        />
      )}

      {/* 목록 */}
      {loaded && items.length > 0 && (
        <>
          <Box
            sx={{
              // 재조회 중에는 리스트를 유지한 채 흐리게 — 검색 디바운스와
              // 결합될 때 화면이 빈 상태로 깜빡이지 않게 한다.
              opacity: loading ? 0.55 : 1,
              pointerEvents: loading ? "none" : "auto",
              transition: "opacity 120ms ease",
            }}
          >
            <LedgerFrame label="피드백 목록" rows={total}>
              {items.map((p, i) => (
                <FeedbackRow
                  key={p.id}
                  post={p}
                  onOpen={(id) => navigate(`${basePath}/${id}`)}
                  isLast={i === items.length - 1}
                />
              ))}
            </LedgerFrame>
          </Box>

          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={pageCount}
                // 서버는 0-base, MUI 는 1-base
                page={(params.page ?? 0) + 1}
                onChange={(_e, p) => setParams({ ...params, page: p - 1 })}
                shape="rounded"
                color="primary"
                siblingCount={0}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%", borderRadius: "8px", fontFamily: T.fonts.sans }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

/* ------------------------------------------------------------------ */

function EmptyState({
  filtered,
  onCreate,
  onReset,
}: {
  filtered: boolean;
  onCreate: () => void;
  onReset: () => void;
}) {
  return (
    <Box
      sx={{
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        py: { xs: 6, md: 8 },
        px: 2,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          mx: "auto",
          mb: 2,
          borderRadius: "12px",
          bgcolor: C.neutral,
          color: C.sub,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {filtered ? (
          <SearchOffIcon sx={{ fontSize: 26 }} />
        ) : (
          <ForumOutlinedIcon sx={{ fontSize: 26 }} />
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: T.fonts.sans,
          fontWeight: 700,
          fontSize: 17,
          color: C.ink,
          mb: 0.75,
        }}
      >
        {filtered ? "조건에 맞는 글이 없습니다" : "아직 등록된 피드백이 없습니다"}
      </Typography>
      <Typography
        sx={{ fontFamily: T.fonts.sans, fontSize: 13.5, color: C.sub, mb: 3 }}
      >
        {filtered
          ? "검색어나 필터를 바꿔 보세요."
          : "앱을 쓰다 불편한 점이나 개선 아이디어를 남겨 주세요."}
      </Typography>

      {filtered ? (
        <Button variant="outlined" onClick={onReset}>
          필터 초기화
        </Button>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={onCreate}
        >
          첫 피드백 남기기
        </Button>
      )}
    </Box>
  );
}
